import React from 'react';
import { McDrawer } from "@maersk-global/mds-react-wrapper/components-core/mc-drawer";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";
import { Box, Typography, Card, CardContent } from '@mui/material';

const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

// IncidentCard component
const IncidentCard = ({ incident }) => {
  return (
    <Card 
      sx={{ 
        mb: 2, 
        border: '1px solid #d9d9d9', 
        boxShadow: 'none',
        borderRadius: '10px',
      }}
    >
      <CardContent sx={{ padding: '16px !important' }}>
        {/* Header with Incident Number and Change Number Badge */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: maerskFontFamily, 
                color: '#0073ab',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
              onClick={() => {
                if (incident.number) {
                  const url = `https://maersk.service-now.com/now/nav/ui/classic/params/target/incident.do%3Fsysparm_query%3Dnumber%3D${incident.number}`;
                  window.open(url, '_blank');
                }
              }}
            >
              {incident.number || 'N/A'}
            </Typography>
          </Box>
          
          {/* Change Number Badge */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'flex-start' }}>     
            <McTag
              label={incident.change_number || 'No Change'}
              appearance="info"
              fit="small"
            />
          </Box>
        </Box>

        {/* Configuration Item and Opened Date - Same Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: maerskFontFamily, 
              color: '#666', 
              fontSize: '0.9rem',
              flex: 1,
              minWidth: 0
            }}
          >
            {incident.incident_ci || 'N/A'}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: maerskFontFamily, 
              fontWeight: 500, 
              color: '#555',
              fontSize: '0.8rem',
              whiteSpace: 'nowrap'
            }}
          >
            Opened: {incident.opened_date || 'N/A'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const IncidentsDrawer = ({
  isIncidentsDrawerOpen,
  incidentsDrawerHeading,
  incidentsDrawerContent,
  onClose
}) => {
    
  return (
    <McDrawer
      open={isIncidentsDrawerOpen}
      closed={onClose}
    >
      <span slot="heading" style={{ color: "#0073ab", fontFamily: maerskFontFamily }}>
        {incidentsDrawerHeading || "Incidents"}
      </span>
      <Box sx={{ mt: 1, overflowY: 'auto', pr: 1 }}>
        {incidentsDrawerContent}
      </Box>
      <McButton slot="footer" dialogaction="close" onClick={onClose}>
        Close
      </McButton>
    </McDrawer>
  );
};

// Export the function so it can be used by the parent component
export { IncidentsDrawer as default };
export const useIncidentsDrawer = (apiData, viewType, selectedPlatform, selectedPortfolio) => {
  const handleIncidentsDrawerOpen = (incidentType) => {
    if (!apiData) return null;

    let incidentsData;
    if (viewType === "applications") {
      incidentsData = apiData.application_incidents;
    } else if (viewType === "platforms") {
      incidentsData = apiData.platform_incidents;
    } else if (viewType === "portfolios") {
      incidentsData = apiData.portfolio_incidents;
    }

    if (!incidentsData) return null;

    let heading = "";
    let drawerContent = null;

    if (incidentType === "p1") {
      const incidents = incidentsData.p1_incidents || [];
      heading = "P1 Incidents";
      drawerContent = (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: "#666", 
              fontSize: '1rem', 
              fontFamily: maerskFontFamily 
            }}
          >
            Total: {incidents.length}
          </Typography>
          {incidents.length > 0 ? (
            <Box>
              {incidents.map((incident, index) => (
                <IncidentCard 
                  key={`p1-${index}-${incident.number}`}
                  incident={incident}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#666", fontFamily: maerskFontFamily }}>
              No P1 incidents found.
            </Typography>
          )}
        </Box>
      );
    } else if (incidentType === "p2") {
      const incidents = incidentsData.p2_incidents || [];
      heading = "P2 Incidents";
      drawerContent = (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: "#666", 
              fontSize: '1rem', 
              fontFamily: maerskFontFamily 
            }}
          >
            Total: {incidents.length}
          </Typography>
          {incidents.length > 0 ? (
            <Box>
              {incidents.map((incident, index) => (
                <IncidentCard 
                  key={`p2-${index}-${incident.number}`}
                  incident={incident}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#666", fontFamily: maerskFontFamily }}>
              No P2 incidents found.
            </Typography>
          )}
        </Box>
      );
    } else if (incidentType === "p3") {
      const incidents = incidentsData.p3_incidents || [];
      heading = "P3 Incidents";
      drawerContent = (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: "#666", 
              fontSize: '1rem', 
              fontFamily: maerskFontFamily 
            }}
          >
            Total: {incidents.length}
          </Typography>
          {incidents.length > 0 ? (
            <Box>
              {incidents.map((incident, index) => (
                <IncidentCard 
                  key={`p3-${index}-${incident.number}`}
                  incident={incident}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#666", fontFamily: maerskFontFamily }}>
              No P3 incidents found.
            </Typography>
          )}
        </Box>
      );
    } else if (incidentType === "p4") {
      const incidents = incidentsData.p4_incidents || [];
      heading = "P4 Incidents";
      drawerContent = (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: "#666", 
              fontSize: '1rem', 
              fontFamily: maerskFontFamily 
            }}
          >
            Total: {incidents.length}
          </Typography>
          {incidents.length > 0 ? (
            <Box>
              {incidents.map((incident, index) => (
                <IncidentCard 
                  key={`p4-${index}-${incident.number}`}
                  incident={incident}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#666", fontFamily: maerskFontFamily }}>
              No P4 incidents found.
            </Typography>
          )}
        </Box>
      );
    }

    return {
      heading,
      drawerContent
    };
  };

  return { handleIncidentsDrawerOpen };
};
